import { useState } from 'react';
import { ProcLocalParams, ProcLocalSummaryExtra, ProcOrigin } from '../../api/assets';
import { useProcessTypeText } from '../../utils/dictOptions';
import { BarList, DislikeRateCell, SortDirToggle, StatCard } from './parts';
import { restoreText } from './processHistoryLabels';
import { satReasonText } from './procLocalText';
import { sortByDir, SortDir } from '../../utils/sortDesc';

// 材料加工 · 读本库后新增的统计卡 (2026-10-20, 历史加工数据并表):
// 数据来源 / 按天趋势 (Studio 实时与 XCS 2.7 历史分色) / 设备模组成功率 / 加工类型 / 满意度 / 不满意原因,
// 以及含历史数据时的元素类型 × 不满意率与耗材还原两张. 卡片的行可点, 下钻到明细.

const ORIGIN_TEXT: Record<string, string> = { studio: 'Studio 实时', history: 'XCS 2.7 历史' };
const STUDIO_COLOR = '#6366f1';
const HISTORY_COLOR = '#f59e0b';
const DAY_CHART_H = 120;

function pct(n: number, d: number): string | undefined {
    return d > 0 ? `${Math.round((n / d) * 100)}%` : undefined;
}

export default function ProcLocalStats({
    sum,
    origin,
    onDrill,
}: {
    sum: ProcLocalSummaryExtra | null;
    origin: ProcOrigin;
    onDrill: (p: ProcLocalParams) => void;
}) {
    const ptText = useProcessTypeText();
    const [elemDir, setElemDir] = useState<SortDir>('desc');
    if (!sum) return null;
    const days = sum.by_day || [];
    const dayMax = Math.max(1, ...days.map((d) => d.studio + d.history));
    const sat = sum.satisfaction;
    const rated = sat ? sat.satisfied + sat.unsatisfied : 0;
    const originTotal = (sum.by_origin || []).reduce((s, o) => s + o.process_count, 0);
    const modules = (sum.by_device_module || []).map((m) => {
        const judged = m.success + m.fail;
        return { ...m, judged, rate: judged > 0 ? Math.round((m.success / judged) * 100) : -1 };
    });
    return (
        <div className="space-y-4">
            {/* 数据来源 + 满意度 */}
            <div className="grid grid-cols-4 gap-4">
                {(sum.by_origin || []).map((o) => (
                    <StatCard key={o.origin} label={ORIGIN_TEXT[o.origin] || o.origin}
                        value={o.process_count.toLocaleString()}
                        sub={originTotal > 0 ? `占全部 ${pct(o.process_count, originTotal)}` : ''} />
                ))}
                {sat && (
                    <StatCard label="用户满意率"
                        value={rated > 0 ? `${Math.round((sat.satisfied / rated) * 100)}%` : '—'}
                        sub={`满意 ${sat.satisfied.toLocaleString()} / 评价 ${rated.toLocaleString()} · 未评价 ${sat.unrated.toLocaleString()} 不计入`} />
                )}
            </div>

            {/* 按天趋势 */}
            {days.length > 0 && (
                <div className="card p-4">
                    <div className="flex items-baseline gap-2 mb-1">
                        <div className="text-sm font-semibold text-slate-700">按天加工量</div>
                        <div className="text-[11px] text-slate-400 ml-auto flex items-center gap-3">
                            <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: STUDIO_COLOR }} />Studio 实时</span>
                            <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: HISTORY_COLOR }} />XCS 2.7 历史</span>
                            <span>{days.length} 天</span>
                        </div>
                    </div>
                    <div className="flex items-end gap-[2px] mt-2" style={{ height: DAY_CHART_H }}>
                        {days.map((d) => {
                            const t = d.studio + d.history;
                            return (
                                <div key={d.date} className="flex-1 group relative flex flex-col justify-end"
                                    style={{ minWidth: 2, height: `${Math.max(2, (t / dayMax) * DAY_CHART_H)}px` }}>
                                    {d.history > 0 && <div style={{ height: `${(d.history / Math.max(1, t)) * 100}%`, background: HISTORY_COLOR }} />}
                                    {d.studio > 0 && <div className="rounded-t-sm" style={{ height: `${(d.studio / Math.max(1, t)) * 100}%`, background: STUDIO_COLOR }} />}
                                    <div className="hidden group-hover:block absolute bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] bg-slate-800 text-white px-1.5 py-0.5 rounded z-10">
                                        {d.date} · 实时 {d.studio.toLocaleString()} · 历史 {d.history.toLocaleString()}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                        <span>{days[0].date}</span><span>{days[days.length - 1].date}</span>
                    </div>
                </div>
            )}

            {/* 设备模组 | 加工类型 */}
            <div className="grid grid-cols-2 gap-4">
                <BarList title="设备模组" hint="机型 × 激光模组 (光源 / 瓦数) · 点击下钻" labelWidth="w-44" maxRows={12}
                    rows={modules.map((m) => ({
                        label: m.device_module || '未知', value: m.process_count,
                        share: m.rate >= 0 ? `达标 ${m.rate}%` : undefined,
                        title: m.judged > 0 ? `已判定 ${m.judged} · 达标 ${m.success} · 不达标 ${m.fail}` : '还没有已判定的加工',
                        onClick: () => onDrill({ device_module: m.device_module }),
                    }))}
                    footer={<div className="text-[11px] text-slate-400">「达标」= 效果完美 + 轻微瑕疵，分母只含已判定的加工（仅 Studio 实时有效果判定）。</div>} />
                <BarList title="加工类型" hint="点击下钻" labelWidth="w-36"
                    rows={(sum.by_processing_type || []).map((p) => ({
                        label: ptText(p.processing_type, '未知'), value: p.process_count,
                        onClick: () => onDrill({ processing_type: p.processing_type }),
                    }))} />
            </div>

            {/* 不满意原因 */}
            {(sum.top_sat_reasons || []).length > 0 && (
                <BarList title="不满意原因" hint="用户评价为不满意时勾选的原因 · 点击下钻" labelWidth="w-36"
                    rows={(sum.top_sat_reasons || []).map((r) => ({
                        label: satReasonText(r.code), value: r.count, title: r.code,
                        onClick: () => onDrill({ sat_reason: r.code }),
                    }))} />
            )}

            {/* 历史数据专属: 元素类型 × 不满意率 | 耗材还原 */}
            {origin !== 'studio' && sum.history && (
                <div className="grid grid-cols-2 gap-4">
                    <div className="card p-4 overflow-x-auto">
                        <div className="flex items-baseline gap-2 mb-2">
                            <div className="text-sm font-semibold text-slate-700">元素类型 × 不满意率</div>
                            <div className="text-[11px] text-slate-400 ml-auto">仅 XCS 2.7 历史 · 点击下钻</div>
                            <SortDirToggle value={elemDir} onChange={setElemDir} />
                        </div>
                        <div className="text-[11px] text-slate-400 mb-2">哪种元素最容易导致差评。不满意率的分母 = 该类型元素里带反馈的条数，样本很小，看趋势别看小数。</div>
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-slate-400 border-b border-slate-100">
                                    <th className="py-1.5">元素类型</th><th className="py-1.5 text-right">元素数</th><th className="py-1.5 text-right">满意</th>
                                    <th className="py-1.5 text-right">不满意</th><th className="py-1.5 text-right pr-2">不满意率</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortByDir(sum.history.element_types || [], (e) => e.count, elemDir).map((e) => (
                                    <tr key={e.element_type} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                                        onClick={() => onDrill({ element_type: e.element_type, origin: 'history' })}>
                                        <td className="py-1.5 font-medium text-slate-700">{e.element_type || '—'}</td>
                                        <td className="py-1.5 text-right font-mono text-slate-600">{e.count.toLocaleString()}</td>
                                        <td className="py-1.5 text-right font-mono text-emerald-600">{e.like == null ? <span className="text-slate-300">—</span> : e.like.toLocaleString()}</td>
                                        <td className="py-1.5 text-right font-mono text-rose-500">{e.dislike.toLocaleString()}</td>
                                        <td className="py-1.5 text-right pr-2">{e.like == null ? <span className="text-slate-300">—</span> : <DislikeRateCell like={e.like} dislike={e.dislike} />}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="space-y-4">
                        <BarList title="还原质检" hint="仅已做耗材还原的历史记录 · 点击下钻" labelWidth="w-24"
                            rows={(sum.history.restore_qc || []).map((r) => ({
                                label: restoreText('qc_verdict', r.code), value: r.count,
                                onClick: () => onDrill({ restore_qc: r.code, origin: 'history' }),
                            }))} />
                        <BarList title="还原表面判定" labelWidth="w-24"
                            rows={(sum.history.restore_finish || []).map((r) => ({
                                label: restoreText('restore_finish', r.code), value: r.count,
                                onClick: () => onDrill({ restore_finish: r.code, origin: 'history' }),
                            }))} />
                    </div>
                </div>
            )}
        </div>
    );
}
